import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import Text from '@atoms/Text/Text';
import Heading from '@atoms/Heading/Heading';
import ConflictListItem from '@molecules/ConflictListItem/ConflictListItem';
import { addListToCartReason, addOrderToCartReason } from '@hooks/useCheckCartConflict';
import {
  StyledCartConflict,
  StyledHeaderNoReplacement,
  StyledCartConflictModal,
  StyledFooter,
  StyledHeader,
  StyledHeading,
  StyledReplaceText,
  StyledScrollableContent,
  StyledHeaderWrapper,
} from './CartConflictModal.styles';
import useCartConflict from './useCartConflict';
import { AxfoodReplacementProductViewModel, UnitData } from '@occ/api-client';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';
import Button from '@atoms/Button/Button';

const CartConflictModal = () => {
  const t = useTranslations('cartConflictModal');
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const {
    onCloseHandler,
    onQuantityChange,
    removeProduct,
    replace,
    resolveAndCloseConflictModal,
    removeAllNonReplaceable,
    onConflictListItemHandler,
    showNonReplaceable,
    nonReplaceableItems,
    currentItemIndex,
    replaceableItems,
    currentItem,
    selectedReplacementItem,
    dontReplaceLoading,
    cartConflictModalBody,
    replaceLoading,
    continueLoading,
    conflictingModal,
  } = useCartConflict();

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (!currentItem?.replacementProductsForProduct) return;

    const itemCount = currentItem.replacementProductsForProduct.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (index + 1) % itemCount;
        itemRefs.current[nextIndex]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = (index - 1 + itemCount) % itemCount;
        itemRefs.current[prevIndex]?.focus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (currentItem?.replacementProductsForProduct?.[index]) {
          onConflictListItemHandler(currentItem.replacementProductsForProduct[index]);
        }
        break;
    }
  };

  const isNonReplaceable = showNonReplaceable && nonReplaceableItems.length >= 1;

  return (
    <StyledCartConflictModal size="medium" hasCloseIcon onClose={onCloseHandler} noPadding>
      <StyledCartConflict data-testid="cart-conflict-modal">
        <StyledHeaderWrapper>
          <StyledHeader>
            {isNonReplaceable ? (
              <>
                <Heading variant="h2" size="small">
                  {t('productsNotAvailable')}
                </Heading>
                <Text size="small" type="body" color="darkerGray">
                  {nonReplaceableItems.length > 1
                    ? `${t('products', { amount: nonReplaceableItems.length })}`
                    : `${t('singleProduct')}`}
                </Text>
              </>
            ) : (
              <>
                <Heading variant="h2" size="small">
                  {t('productNotInStock')}
                </Heading>
                <Text size="small" type="body" color="darkerGray">{`${currentItemIndex + 1} / ${
                  replaceableItems.length
                }`}</Text>
              </>
            )}

            <StyledHeading variant="h3" label>
              {t('outOfStock')}
            </StyledHeading>
          </StyledHeader>

          {showNonReplaceable && !currentItem?.partialOutOfStock && (
            <StyledHeaderNoReplacement>
              <Text align="left" type="body">
                {t('replacementItemsCouldNotBeFound')}
              </Text>
            </StyledHeaderNoReplacement>
          )}
          {currentItem && (
            <ConflictListItem
              product={currentItem.entryProduct as AxfoodReplacementProductViewModel & { pickUnit?: UnitData }}
              pickQuantity={currentItem.pickQuantity as number}
              availableQuantity={currentItem.availableQuantity as number}
              partialOutOfStock={currentItem.partialOutOfStock}
              variant="conflicting"
            />
          )}
        </StyledHeaderWrapper>
        <ScrollShadow>
          <StyledScrollableContent ref={cartConflictModalBody}>
            {!showNonReplaceable && (
              <StyledReplaceText>
                <Heading variant="h3" label>
                  {t('replaceWith')}
                </Heading>

                <Text type="detail" color="red">
                  {t('sizeVariation')}
                </Text>
              </StyledReplaceText>
            )}
            {currentItem && !showNonReplaceable && (
              <>
                {currentItem.replacementProductsForProduct?.map((item: any, index: number) => (
                  <ConflictListItem
                    key={`replaceable-item-${currentItem.entryProduct?.code}-${item.code}`}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    editable
                    product={item}
                    pickQuantity={item.pickQuantity}
                    availableQuantity={item.availableQuantity}
                    variant="replaceable"
                    onClick={() => {
                      onConflictListItemHandler(item);
                    }}
                    onQuantityChange={(qty: number) => {
                      onQuantityChange(qty, item);
                    }}
                    selected={selectedReplacementItem?.code === item.code}
                    onKeyDown={(e) => handleItemKeyDown(e, index)}
                  />
                ))}
              </>
            )}
            {showNonReplaceable && !currentItem && (
              <>
                {nonReplaceableItems.map((item) => (
                  <ConflictListItem
                    key={item.entryProduct?.code}
                    product={item.entryProduct as AxfoodReplacementProductViewModel & { pickUnit?: UnitData }}
                    variant="non-replaceable"
                    partialOutOfStock={item.partialOutOfStock}
                    pickQuantity={item.pickQuantity as number}
                    availableQuantity={item.availableQuantity as number}
                    addToCartDisabled={item.entryProduct?.addToCartDisabled}
                  />
                ))}
              </>
            )}
          </StyledScrollableContent>
        </ScrollShadow>

        <StyledFooter>
          {!showNonReplaceable ? (
            <>
              <Button variant="secondary" fullWidth onClick={() => removeProduct()} isSubmitting={dontReplaceLoading}>
                {t('dontReplace')}
              </Button>
              <Button
                fullWidth
                onClick={replace}
                disabled={!showNonReplaceable && !selectedReplacementItem}
                isSubmitting={replaceLoading}
              >
                {t('replace')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" fullWidth onClick={onCloseHandler} isSubmitting={continueLoading}>
                {t('cancel')}
              </Button>
              <Button
                fullWidth
                onClick={() =>
                  conflictingModal?.reason === addOrderToCartReason || conflictingModal?.reason === addListToCartReason
                    ? resolveAndCloseConflictModal()
                    : removeAllNonReplaceable()
                }
                isSubmitting={continueLoading}
              >
                {t('continue')}
              </Button>
            </>
          )}
        </StyledFooter>
      </StyledCartConflict>
    </StyledCartConflictModal>
  );
};

export default CartConflictModal;
